import mapboxgl from 'mapbox-gl';

import { MarkerManager } from './mapTypes';

function cloneImageElement(img: HTMLImageElement): HTMLImageElement {
  const cloned = new Image();
  cloned.src = img.src;
  cloned.width = img.width;
  cloned.height = img.height;
  cloned.style.width = img.style.width || `${img.width}px`;
  cloned.style.height = img.style.height || `${img.height}px`;
  return cloned;
}

export function createMarkerManager(): MarkerManager {
  const markers = new Map<string, mapboxgl.Marker>();
  const created = new Set<string>();
  const lastImages = new Map<string, HTMLImageElement>();

  const createMarker = (
    id: string,
    image: HTMLImageElement,
    position: [number, number],
    map: mapboxgl.Map,
    rotation?: number
  ): mapboxgl.Marker | null => {
    const existingMarker = markers.get(id);
    if (existingMarker && existingMarker.getElement()?.isConnected) {
      return existingMarker;
    }

    if (created.has(id)) {
      return markers.get(id) || null;
    }

    const clonedElement = cloneImageElement(image);
    const marker = new mapboxgl.Marker({ element: clonedElement }).setLngLat(position).addTo(map);

    if (rotation !== undefined) {
      marker.setRotation(rotation);
    }

    markers.set(id, marker);
    created.add(id);
    lastImages.set(id, image);

    return marker;
  };

  const updateMarker = (id: string, position: [number, number], rotation?: number): void => {
    const marker = markers.get(id);
    if (!marker) return;

    marker.setLngLat(position);
    if (rotation !== undefined) {
      marker.setRotation(rotation);
    }
  };

  const removeMarker = (id: string): void => {
    const marker = markers.get(id);
    if (marker) {
      marker.remove();
      markers.delete(id);
      created.delete(id);
      lastImages.delete(id);
    }
  };

  const removeAllMarkers = (): void => {
    markers.forEach((marker) => marker.remove());
    markers.clear();
    created.clear();
    lastImages.clear();
  };

  const hasMarker = (id: string): boolean => markers.has(id);

  const getMarker = (id: string): mapboxgl.Marker | undefined => markers.get(id);

  const shouldRecreateMarker = (id: string, newImage: HTMLImageElement): boolean => {
    const lastImage = lastImages.get(id);
    return lastImage !== undefined && lastImage !== newImage;
  };

  return {
    markers,
    created,
    lastImages,
    createMarker,
    updateMarker,
    removeMarker,
    removeAllMarkers,
    hasMarker,
    getMarker,
    shouldRecreateMarker
  };
}
